import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import {
  clx,
  showErrorMsg,
  showSuccessMsg,
  showWarningMsg,
} from "../../../../../utils/Helpers";
import UIModal from "../../../../ui/UIModal";
import UINumberField from "../../../../ui/UINumberField";
import UISelect from "../../../../ui/UISelect";
import {
  PAYROLL_ADJUSTMENT_API_TYPES,
  PAYROLL_ADJUSTMENT_TYPES,
  PAYROLL_REASON_TYPE_FILTERS,
} from "../constants/constants";
import { getCompanyReason, getReason } from "../../../../../network/apis/reason";
import {
  bulkCreateAdminPayrollAdjustments,
  bulkCreateCompanyPayrollAdjustments,
  createAdminPayrollAdjustment,
  createCompanyPayrollAdjustment,
} from "../../../../../network/apis/payrollAdjustments";

import "./EventPayrollManualAdjustmentModal.scss";

const AMOUNT_PLACEHOLDER = "0";

const parseAmount = (value) => {
  const parsed = Number.parseInt(String(value || "").trim(), 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const EventPayrollManualAdjustmentModal = ({
  open,
  type,
  applicantIds,
  workerName,
  isGiveAll,
  successMessage,
  eventId,
  portal,
  labels,
  shared,
  lang,
  onClose,
  onSuccess,
}) => {
  const [amount, setAmount] = useState("");
  const [selectedReason, setSelectedReason] = useState(null);
  const [amountBlurred, setAmountBlurred] = useState(false);
  const [reasonBlurred, setReasonBlurred] = useState(false);
  const [confirmAttempted, setConfirmAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [reasonsError, setReasonsError] = useState(false);
  const [reasonsErrorMessage, setReasonsErrorMessage] = useState("");

  const isReward = type === PAYROLL_ADJUSTMENT_TYPES.reward;
  const typeLabels = isReward ? labels.reward : labels.deduction;
  const modalType = isReward ? "success" : "destructive";
  const applicantCount = applicantIds.length;
  const isBulk = applicantCount > 1;
  const currencyLabel = shared.sar || "";
  const amountLabel = labels.amountLabel.replace("{currency}", currencyLabel);

  const reasonOptions = useMemo(
    () =>
      reasons.map((reason) => ({
        value: reason.id,
        label: lang === "ar" ? reason.title_ar : reason.title_en,
      })),
    [reasons, lang],
  );

  const selectedReasonOption = useMemo(
    () =>
      reasonOptions.find((option) => option.value === selectedReason) || null,
    [reasonOptions, selectedReason],
  );

  const parsedAmount = parseAmount(amount);

  useEffect(() => {
    if (!open) {
      setAmount("");
      setSelectedReason(null);
      setAmountBlurred(false);
      setReasonBlurred(false);
      setConfirmAttempted(false);
      setSubmitting(false);
      setReasons([]);
      setReasonsLoading(false);
      setReasonsError(false);
      setReasonsErrorMessage("");
      return;
    }

  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    const fetchReasons = portal === "company" ? getCompanyReason : getReason;
    const reasonTypeFilter = isReward
      ? PAYROLL_REASON_TYPE_FILTERS.reward
      : PAYROLL_REASON_TYPE_FILTERS.deduction;

    setReasonsLoading(true);
    setReasonsError(false);
    setReasonsErrorMessage("");
    fetchReasons({
      reason_type: reasonTypeFilter,
      page: -1,
    })
      .then((response) => {
        if (cancelled) return;
        setReasons(response?.data?.data || []);
        setReasonsError(false);
        setReasonsErrorMessage("");
      })
      .catch(() => {
        if (!cancelled) {
          setReasons([]);
          setReasonsError(true);
          setReasonsErrorMessage(labels.validation.reasonsLoadFailed);
          showWarningMsg(labels.validation.reasonsLoadFailed);
        }
      })
      .finally(() => {
        if (!cancelled) setReasonsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isReward, portal, lang]);

  const amountError =
    parsedAmount > 0 ? null : labels.validation.amountRequired;
  const reasonError = selectedReason ? null : labels.validation.reasonRequired;
  const reasonErrorMessage = reasonsError
    ? reasonsErrorMessage || labels.validation.reasonsLoadFailed
    : reasonError;

  const showAmountError = Boolean(amountError && (amountBlurred || confirmAttempted));
  const showReasonError = Boolean(reasonsError || (reasonError && (reasonBlurred || confirmAttempted)));

  const isFormValid = parsedAmount > 0 && Boolean(selectedReason);

  const description = isGiveAll
    ? typeLabels.subtitleAll
    : isBulk
      ? typeLabels.subtitleBulk.replace("{count}", String(applicantCount))
      : typeLabels.subtitleSingle.replace("{name}", workerName || "-");

  const handleRequestClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleAdjustmentConfirm = async () => {
    const baseParams = {
      amount: parsedAmount,
      adjustment_type: isReward
        ? PAYROLL_ADJUSTMENT_API_TYPES.reward
        : PAYROLL_ADJUSTMENT_API_TYPES.deduction,
      payroll_reason_id: selectedReason,
    };

    if (isBulk) {
      const bulkCreate =
        portal === "company"
          ? bulkCreateCompanyPayrollAdjustments
          : bulkCreateAdminPayrollAdjustments;

      const response = await bulkCreate({
        eventId,
        params: { ...baseParams, user_ids: applicantIds },
      });

      if (response?.status === 207) {
        showWarningMsg(response?.data?.message || typeLabels.successPartial);
        return;
      }

      const bulkMessage = typeLabels.successBulk.replace(
        "{count}",
        String(applicantCount),
      );

      showSuccessMsg(successMessage || bulkMessage, true);
      return;
    }

    const create =
      portal === "company" ? createCompanyPayrollAdjustment : createAdminPayrollAdjustment;

    await create({
      eventId,
      params: { ...baseParams, user_id: applicantIds[0] },
    });
    showSuccessMsg(typeLabels.successSingle, true);
  };

  const handleConfirm = async () => {
    setConfirmAttempted(true);
    if (!isFormValid) return;

    setSubmitting(true);
    try {
      await handleAdjustmentConfirm();

      onSuccess?.();
      onClose();
    } catch (error) {
      showErrorMsg(error.response?.data?.message || shared.generalErrMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <UIModal
      open={open}
      type={modalType}
      title={typeLabels.title}
      description={description}
      cancelLabel={shared.cancel}
      confirmLabel={typeLabels.apply}
      onClose={handleRequestClose}
      onCancel={handleRequestClose}
      onConfirm={handleConfirm}
      cancelButtonProps={{ disabled: submitting }}
      confirmButtonProps={{
        disabled: submitting || !isFormValid || reasonsLoading,
      }}
      PaperProps={{
        className: clx(
          "event-payroll-manual-adjustment-modal",
          `event-payroll-manual-adjustment-modal--${type}`,
        ),
      }}
      aria-labelledby="event-payroll-manual-adjustment-modal-title"
      disableEscapeKeyDown={submitting}
    >
      <div className="event-payroll-manual-adjustment-modal__fields">
        <UINumberField
          id="event-payroll-adjustment-amount"
          name="amount"
          label={amountLabel}
          required
          value={amount}
          onValueChange={setAmount}
          onBlur={() => setAmountBlurred(true)}
          placeholder={AMOUNT_PLACEHOLDER}
          min={1}
          step={1}
          inputMode="numeric"
          disabled={submitting}
          error={showAmountError}
          helperText={showAmountError ? amountError : undefined}
        />
        <UISelect
          id="event-payroll-adjustment-reason"
          name="reason"
          label={typeLabels.reasonLabel}
          required
          placeholder={labels.chooseReason}
          options={reasonOptions}
          value={selectedReasonOption}
          onChange={(option) => setSelectedReason(option?.value || null)}
          onBlur={() => setReasonBlurred(true)}
          getOptionLabel={(option) => option?.label || ""}
          isOptionEqualToValue={(option, value) =>
            option?.value === value?.value
          }
          disabled={submitting || reasonsLoading}
          error={showReasonError}
          helperText={showReasonError ? reasonErrorMessage : undefined}
        />
      </div>
    </UIModal>
  );
};

EventPayrollManualAdjustmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  type: PropTypes.oneOf([
    PAYROLL_ADJUSTMENT_TYPES.reward,
    PAYROLL_ADJUSTMENT_TYPES.deduction,
  ]),
  applicantIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ).isRequired,
  workerName: PropTypes.string,
  isGiveAll: PropTypes.bool,
  successMessage: PropTypes.string,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  portal: PropTypes.oneOf(["admin", "company"]),
  labels: PropTypes.object.isRequired,
  shared: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

EventPayrollManualAdjustmentModal.defaultProps = {
  type: PAYROLL_ADJUSTMENT_TYPES.reward,
  workerName: null,
  isGiveAll: false,
  successMessage: undefined,
  eventId: null,
  portal: "admin",
  onSuccess: undefined,
};

export default EventPayrollManualAdjustmentModal;
